import { makeAutoObservable, runInAction } from 'mobx'
import { AnonEvent } from '../types/Event'
import { EventService } from '../services/eventService'
import { RootStore } from './rootStore'
import { ToastType } from '../types/ToastData'

const getErrorMessage = (error: unknown): string | undefined => (error as { message?: string })?.message ?? (error ? String(error) : undefined)

export class EventStore {
    private eventService: EventService
    private rootStore: RootStore
    events: AnonEvent[] = []
    // Admin-scoped events are kept apart from `events` (the caller's own company) rather than sharing
    // one flat cache - otherwise an admin who'd already loaded their own or another company's events
    // could see them reused while editing a different company's conversions. adminEventsCompanyId marks
    // which company adminEvents belongs to, so a stale cross-company read is never served.
    adminEvents: AnonEvent[] = []
    adminEventsCompanyId: number | null = null
    error: string | null = null

    constructor(rootStore: RootStore, eventService: EventService) {
        this.rootStore = rootStore
        this.eventService = eventService
        makeAutoObservable(this)
    }

    loadEvents = async (forceRefresh = false, companyId?: number) => {
        if (companyId) {
            if (!forceRefresh && this.adminEventsCompanyId === companyId) return this.adminEvents
            return this.getEvents(companyId)
        }
        if (this.events.length > 0 && !forceRefresh) return this.events
        return this.getEvents()
    }

    // Returns only events known to belong to the requested scope - never falls back to a
    // different company's (or the admin's own) cached events while the right scope is loading.
    eventsFor(companyId?: number): AnonEvent[] {
        if (!companyId) return this.events
        return this.adminEventsCompanyId === companyId ? this.adminEvents : []
    }

    async getEvents(companyId?: number) {
        runInAction(() => {
            this.error = null
        })
        try {
            this.rootStore.appStore.incrementRequests()
            const events = companyId ? await this.eventService.getEventsAdmin(companyId) : await this.eventService.getEvents()
            runInAction(() => {
                if (companyId) {
                    this.adminEvents = events
                    this.adminEventsCompanyId = companyId
                } else {
                    this.events = events
                }
            })
            return events
        } catch (error) {
            runInAction(() => {
                const errMsg = getErrorMessage(error)
                this.rootStore.appStore.setToastNotification({
                    type: ToastType.Error,
                    title: 'Failed to get events',
                    body: errMsg ?? 'Something went wrong, please try again'
                })
                this.error = errMsg ?? null
            })
        } finally {
            this.rootStore.appStore.decrementRequests()
        }
    }

    async create(event: Omit<AnonEvent, 'id' | 'createdAt' | 'updatedAt'>): Promise<AnonEvent | null> {
        try {
            const result = await this.eventService.createEvent(event)
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Success,
                title: 'Created',
                body: 'Event was successfully created',
                isHidden: true
            })
            return result
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to create event',
                body: getErrorMessage(error) ?? 'Something went wrong, please try again'
            })
            return null
        }
    }

    async update(id: number, event: Omit<AnonEvent, 'id' | 'createdAt' | 'updatedAt'>): Promise<AnonEvent | null> {
        try {
            const result = await this.eventService.updateEvent(id, event)
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Success,
                title: 'Updated',
                body: 'Event was successfully updated',
                isHidden: true
            })
            return result
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to update event',
                body: getErrorMessage(error) ?? 'Something went wrong, please try again'
            })
            return null
        }
    }

    async remove(id: number): Promise<boolean> {
        try {
            await this.eventService.removeEvent(id)

            this.rootStore.appStore.setToastNotification({
                type: ToastType.Success,
                title: 'Removed',
                body: 'Event was successfully removed',
                isHidden: true
            })
            return true
        } catch (error) {
            this.rootStore.appStore.setToastNotification({
                type: ToastType.Error,
                title: 'Failed to remove event',
                body: getErrorMessage(error) ?? 'Something went wrong, please try again'
            })
            return false
        }
    }
}
